import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { fetchTasks as fetchTasksFn, fetchCrmEntities, fetchTenantUsers, fetchHorses } from '../../lib/queryFunctions';
import { useAuth } from '../../context/AuthContext';
import { activityAPI } from '../../services/api';
import api from '../../services/api';
import SidebarLayout from '../../components/layout/SidebarLayout';
import HorseshoeIcon from '../../components/icons/HorseshoeIcon';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Loader2, Search, CheckCircle2, Circle, Clock, AlertCircle, XCircle, Building2, Contact, Plus, Users, PauseCircle } from 'lucide-react';
import { TableToolbar } from '../../components/common/TableToolbar';
import { TaskDetailDialog, TaskDialog } from '../../components/crm/ActivityDialogs';
import { toast } from 'sonner';

const TaskStatusIndicator = ({ status }) => {
  const statusConfig = {
    not_started: { Icon: Circle, className: 'text-gray-400' },
    in_progress: { Icon: Clock, className: 'text-blue-500' },
    on_hold: { Icon: PauseCircle, className: 'text-yellow-500' },
    completed: { Icon: CheckCircle2, className: 'text-green-500' },
    cancelled: { Icon: XCircle, className: 'text-red-500' },
  };
  const config = statusConfig[status] || statusConfig.not_started;
  const Icon = config.Icon;
  return <Icon className={`w-5 h-5 shrink-0 ${config.className}`} />;
};

const TasksPage = () => {
  const { user, isViewOnly } = useAuth();
  const viewOnly = isViewOnly();
  const isTeamLeader = user?.roles?.includes('tenant_team_leader');
  const qc = useQueryClient();

  // ── UI state ──────────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [assignedFilter, setAssignedFilter] = useState('all');
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskDetailOpen, setTaskDetailOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [showDeptView, setShowDeptView] = useState(false);

  // ── Server state ──────────────────────────────────────────────────────────
  const taskScope = showDeptView ? 'department' : 'my';

  const { data: rawTasks = [], isLoading: loading } = useQuery({
    queryKey: QK.tasksList(taskScope),
    queryFn: fetchTasksFn(taskScope),
    onError: () => toast.error('Failed to load tasks'),
  });

  // Sort and enrich tasks
  const tasks = useMemo(() => {
    const sorted = [...rawTasks].sort((a, b) => {
      const aStr = a.end_date_time ? String(a.end_date_time).split('T')[0] : 'z';
      const bStr = b.end_date_time ? String(b.end_date_time).split('T')[0] : 'z';
      return aStr.localeCompare(bStr);
    });
    return sorted;
  }, [rawTasks]);

  const { data: usersData = [] } = useQuery({
    queryKey: QK.tenantUsers(),
    queryFn: fetchTenantUsers(),
  });
  const users = usersData;

  const { data: allEntities = [] } = useQuery({
    queryKey: QK.crmEntities(),
    queryFn: fetchCrmEntities(),
  });
  const entities = useMemo(() => Object.fromEntries(allEntities.map(e => [e.id, e.system_name || e.name || 'Unknown'])), [allEntities]);
  const allAccounts = useMemo(() => allEntities.filter(e => e.crm_class === 'account'), [allEntities]);

  const { data: allHorsesData = [] } = useQuery({
    queryKey: QK.horses({ include_archived: true }),
    queryFn: fetchHorses({ include_archived: true }),
  });
  const allHorses = allHorsesData;
  const horses = useMemo(() => Object.fromEntries(allHorsesData.filter(h => tasks.some(t => t.horse_id === h.id)).map(h => [h.id, h.system_name || h.name])), [allHorsesData, tasks]);

  const invalidateTasks = () => qc.invalidateQueries({ queryKey: QK.tasksList(taskScope) });

  // ── Filtered tasks ────────────────────────────────────────────────────────
  const filteredTasks = useMemo(() => {
    let filtered = [...tasks];
    if (searchTerm) {
      filtered = filtered.filter(t =>
        t.activity_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (statusFilter !== 'all') filtered = filtered.filter(t => t.task_status === statusFilter);
    if (assignedFilter === 'me')      filtered = filtered.filter(t => t.assigned_to_user_id === user?.id);
    else if (assignedFilter === 'created') filtered = filtered.filter(t => t.created_by_id === user?.id);
    return filtered;
  }, [tasks, searchTerm, statusFilter, assignedFilter, user?.id]);

  const handleTaskClick = (task) => { setSelectedTask(task); setTaskDetailOpen(true); };

  const handleTaskUpdate = () => { setTaskDetailOpen(false); invalidateTasks(); };

  const handleMarkComplete = async (taskId, e) => {
    e.stopPropagation();
    try {
      await activityAPI.update(taskId, { task_status: 'completed' });
      toast.success('Task marked as completed');
      invalidateTasks();
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    }
  };

  const getUserName = (userId) => {
    const u = users.find(user => user.id === userId);
    return u ? `${u.first_name} ${u.last_name}` : 'Unknown';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <SidebarLayout>
        <div className="flex justify-center items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="font-serif text-3xl font-semibold text-foreground">
              {showDeptView ? 'Department Tasks' : 'All Tasks'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {showDeptView ? 'Tasks created by or assigned to your department' : 'Manage and track all your tasks'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isTeamLeader && (
              <div className="flex items-center gap-2" data-testid="dept-view-toggle">
                <Switch
                  id="tasks-dept-switch"
                  checked={showDeptView}
                  onCheckedChange={setShowDeptView}
                />
                <Label htmlFor="tasks-dept-switch" className="text-sm cursor-pointer flex items-center gap-1.5 select-none">
                  <Users className="w-3.5 h-3.5" />
                  Department View
                </Label>
              </div>
            )}
            {!viewOnly && (
              <Button onClick={() => setTaskDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Task
              </Button>
            )}
          </div>
        </div>

        {/* Tasks List */}
        <Card>
          <CardHeader className="pb-4 bg-muted/20">
            {/* Filters */}
            <div className="flex flex-col gap-2">
              {/* Search — full width */}
              <div className="relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search tasks..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 bg-white w-full"
                />
              </div>

              {/* Status + Assigned filters — full width on mobile, side by side on desktop */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="bg-white w-full">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="not_started">Not Started</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="on_hold">On Hold</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={assignedFilter} onValueChange={setAssignedFilter}>
                  <SelectTrigger className="bg-white w-full">
                    <SelectValue placeholder="All Tasks" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tasks</SelectItem>
                    <SelectItem value="me">Assigned to Me</SelectItem>
                    <SelectItem value="created">Created by Me</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Export CSV — full width on mobile, right-aligned on desktop */}
              <div className="flex justify-end">
                <TableToolbar
                  inline
                  tableId="tasks-list"
                  data={filteredTasks}
                  filename="tasks"
                  showColumns={false}
                  columns={[
                    { key: 'title', label: 'Title' },
                    { key: 'description', label: 'Description' },
                    { key: 'task_status', label: 'Status' },
                    { key: 'task_priority', label: 'Priority' },
                    { key: 'assigned_to_name', label: 'Assigned To' },
                    { key: 'end_date_time', label: 'Due Date', accessor: r => r.end_date_time ? new Date(r.end_date_time).toLocaleDateString() : '-' },
                    { key: 'entity_name', label: 'Related To' },
                  ]}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-2">
            {filteredTasks.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-20" />
                <p>No tasks found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredTasks.map((task) => {
                  const isOverdue = task.end_date_time && new Date(task.end_date_time) < new Date() && task.task_status !== 'completed' && task.task_status !== 'cancelled';

                  return (
                    <div
                      key={task.id}
                      onClick={() => handleTaskClick(task)}
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 10px',
                        border: `1px solid ${isOverdue ? 'rgb(239 68 68)' : 'hsl(var(--border))'}`,
                        borderRadius: '6px',
                        cursor: 'pointer',
                        width: '100%',
                        boxSizing: 'border-box',
                      }}
                    >
                      {/* Checkmark */}
                      <div
                        onClick={(e) => handleMarkComplete(task.id, e)}
                        style={{ flexShrink: 0, cursor: 'pointer', width: '22px', height: '22px' }}
                      >
                        <svg viewBox="0 0 24 24" fill="none" style={{ width: '22px', height: '22px', color: '#9ca3af' }}>
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                          <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.3" />
                        </svg>
                      </div>
                      {/* Task content */}
                      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px' }}>
                          <p style={{ fontWeight: 500, fontSize: '14px', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.activity_name}</p>
                          <TaskStatusIndicator status={task.task_status} />
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 12px', fontSize: '12px', color: 'hsl(var(--muted-foreground))', marginTop: '2px' }}>
                          {(task.crm_entity_id || task.linked_account_id) && entities[(task.crm_entity_id || task.linked_account_id)] && (
                            <span style={{ color: 'hsl(var(--primary))', display: 'flex', alignItems: 'center', gap: '2px' }}>
                              <Building2 className="w-3 h-3" />
                              {entities[(task.crm_entity_id || task.linked_account_id)]}
                            </span>
                          )}
                          {task.horse_id && horses[task.horse_id] && (
                            <span style={{ color: 'hsl(var(--primary))', display: 'flex', alignItems: 'center', gap: '2px' }}>
                              <HorseshoeIcon className="w-3 h-3" />
                              {horses[task.horse_id]}
                            </span>
                          )}
                          <span>Due: {task.end_date_time ? formatDate(task.end_date_time) : 'None'}</span>
                          {task.assigned_to_user_id && (
                            <span>Assigned: {getUserName(task.assigned_to_user_id)}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Task Detail Dialog */}
      {selectedTask && (
        <TaskDetailDialog
          open={taskDetailOpen}
          onOpenChange={setTaskDetailOpen}
          task={selectedTask}
          onSuccess={() => {
            setTaskDetailOpen(false);
            setSelectedTask(null);
            invalidateTasks();
          }}
          onTaskUpdate={(updatedTask) => {
            setSelectedTask(updatedTask);
            invalidateTasks();
          }}
          entities={entities}
          users={users}
        />
      )}
      
      {/* Task Creation Dialog */}
      <TaskDialog
        open={taskDialogOpen}
        onOpenChange={setTaskDialogOpen}
        accountId={null}
        horseId={null}
        onSuccess={() => {
          setTaskDialogOpen(false);
          invalidateTasks();
        }}
        associations={[]}
        users={users}
        accounts={allAccounts}
        horses={allHorses}
        showAccountDropdown={true}
      />
    </SidebarLayout>
  );
};

export default TasksPage;
